/**
 * @fileoverview Modal de TROCA de cartão da assinatura (seção "Forma de
 * pagamento" da aba Faturamento). O cartão é tokenizado NO BROWSER (util
 * cardForm) — PAN/CVV nunca passam pelo nosso servidor — e o token é enviado a
 * POST /account/payment-method, que cria o cartão e aponta a assinatura pra ele.
 */

import React, { useState } from "react";
import { Dialog, makeStyles } from "@material-ui/core";
import { X, CreditCard, Loader, CheckCircle2, ShieldCheck } from "lucide-react";

import api from "../../../services/api";
import { i18n } from "../../../translate/i18n";
import { createModalDesign } from "../../../styles/modalDesign";
import {
  onlyDigits, detectBrand, maskedNumber, formatExpiry, tokenizeCard, hasPagarmePublicKey
} from "../../../utils/cardForm";

const BRANDS = ["visa", "mastercard", "amex", "elo", "hipercard", "diners"];

const useStyles = makeStyles(theme => {
  const md = createModalDesign(theme);
  const dark = theme.palette.type === "dark";
  const border = dark ? "rgba(255,255,255,0.1)" : "#e5e7eb";
  return {
    dialog: { ...md.mdDialog, "& .MuiDialog-paper": { ...md.mdDialog["& .MuiDialog-paper"], width: "min(460px, calc(100vw - 32px))" } },
    head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: theme.spacing(3.5, 3.5, 0) },
    title: { fontSize: "1.25rem", fontWeight: 700, color: dark ? "#f8fafc" : "#111827", display: "flex", alignItems: "center", gap: 8 },
    desc: { fontSize: "0.85rem", color: dark ? "rgba(248,250,252,0.65)" : "#6b7280", marginTop: 6, lineHeight: 1.5 },
    close: { cursor: "pointer", color: dark ? "rgba(248,250,252,0.6)" : "#9ca3af", display: "flex" },
    body: { padding: theme.spacing(2.5, 3.5, 3.5), display: "flex", flexDirection: "column", gap: theme.spacing(1.75) },
    field: { display: "flex", flexDirection: "column", gap: 5 },
    label: { fontSize: "0.8rem", fontWeight: 600, color: dark ? "#f8fafc" : "#111827" },
    input: { height: 42, borderRadius: 8, border: `1px solid ${border}`, background: dark ? "rgba(255,255,255,0.05)" : "#fff", color: dark ? "#f8fafc" : "#111827", padding: "0 12px", fontSize: "0.95rem", fontFamily: "inherit", outline: "none", "&:focus": { borderColor: "#3D5CFD" } },
    grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: theme.spacing(1) },
    brandsRow: { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" },
    brandChip: { fontSize: "0.6rem", fontWeight: 700, padding: "3px 7px", borderRadius: 5, border: `1px solid ${border}`, color: dark ? "rgba(248,250,252,0.55)" : "#6b7280", letterSpacing: 0.3, opacity: 0.55, transition: "all 120ms" },
    brandChipActive: { opacity: 1, borderColor: "#3D5CFD", color: "#3D5CFD", background: dark ? "rgba(61,92,253,0.14)" : "rgba(61,92,253,0.06)" },
    cta: { ...md.mdBtnPrimary, height: 46, width: "100%", fontSize: "0.98rem", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: theme.spacing(0.5) },
    error: { fontSize: "0.82rem", color: "#ef4444" },
    secure: { fontSize: "0.74rem", color: dark ? "rgba(248,250,252,0.5)" : "#9ca3af", display: "flex", alignItems: "center", gap: 5 },
    center: { textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: theme.spacing(1.5), padding: theme.spacing(2, 0) },
    successIcon: { color: "#22c55e" }
  };
});

const ChangeCardModal = ({ open, onClose, onChanged }) => {
  const classes = useStyles();
  const [card, setCard] = useState({ number: "", holder: "", expiry: "", cvv: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!open) return null;

  const brand = detectBrand(card.number);

  const reset = () => { setCard({ number: "", holder: "", expiry: "", cvv: "" }); setError(""); setDone(false); };
  const close = () => { if (submitting) return; reset(); onClose && onClose(); };

  const handleSave = async () => {
    setError("");
    if (!hasPagarmePublicKey()) { setError(i18n.t("onboarding.changeCard.notConfigured")); return; }
    if (!card.number || !card.holder || onlyDigits(card.expiry).length < 4 || !card.cvv) {
      setError(i18n.t("onboarding.changeCard.fillAllFields")); return;
    }
    setSubmitting(true);
    try {
      const token = await tokenizeCard(card);
      await api.post("/account/payment-method", { card_token: token });
      setDone(true);
      if (onChanged) { try { await onChanged(); } catch (_) { /* refetch é best-effort */ } }
      setTimeout(() => { reset(); onClose && onClose(); }, 1400);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && (detail.message || (typeof detail === "string" ? detail : JSON.stringify(detail)))) || e?.response?.data?.error || e.message;
      setError(i18n.t("onboarding.changeCard.saveError") + (msg ? `: ${msg}` : "."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open disableEscapeKeyDown className={classes.dialog} maxWidth={false} onClose={close}>
      {!done && (
        <div className={classes.head}>
          <div>
            <div className={classes.title}><CreditCard size={20} /> {i18n.t("onboarding.changeCard.title")}</div>
            <div className={classes.desc}>{i18n.t("onboarding.changeCard.desc")}</div>
          </div>
          <span className={classes.close} onClick={close} title={i18n.t("onboarding.changeCard.close")}><X size={20} /></span>
        </div>
      )}

      <div className={classes.body}>
        {done ? (
          <div className={classes.center}>
            <CheckCircle2 size={52} className={classes.successIcon} />
            <div className={classes.label} style={{ fontSize: "1.05rem" }}>{i18n.t("onboarding.changeCard.successTitle")}</div>
          </div>
        ) : (
          <>
            <div className={classes.field}>
              <span className={classes.label}>{i18n.t("onboarding.changeCard.numberLabel")}</span>
              <input className={classes.input} inputMode="numeric" autoComplete="cc-number" placeholder="0000 0000 0000 0000"
                value={maskedNumber(card.number).replace(/•/g, "").trimEnd()}
                onChange={e => setCard({ ...card, number: onlyDigits(e.target.value).slice(0, 19) })} />
            </div>
            <div className={classes.field}>
              <span className={classes.label}>{i18n.t("onboarding.changeCard.holderLabel")}</span>
              <input className={classes.input} autoComplete="cc-name" placeholder={i18n.t("onboarding.changeCard.holderPlaceholder")}
                value={card.holder} onChange={e => setCard({ ...card, holder: e.target.value.toUpperCase() })} />
            </div>
            <div className={classes.grid2}>
              <div className={classes.field}>
                <span className={classes.label}>{i18n.t("onboarding.changeCard.expiryLabel")}</span>
                <input className={classes.input} inputMode="numeric" autoComplete="cc-exp" placeholder={i18n.t("onboarding.changeCard.expiryPlaceholder")}
                  value={card.expiry} onChange={e => setCard({ ...card, expiry: formatExpiry(e.target.value) })} />
              </div>
              <div className={classes.field}>
                <span className={classes.label}>{i18n.t("onboarding.changeCard.cvvLabel")}</span>
                <input className={classes.input} inputMode="numeric" autoComplete="cc-csc" placeholder="123" maxLength={4}
                  value={card.cvv} onChange={e => setCard({ ...card, cvv: onlyDigits(e.target.value) })} />
              </div>
            </div>

            <div className={classes.brandsRow}>
              {BRANDS.map(b => (
                <span key={b} className={`${classes.brandChip} ${brand === b ? classes.brandChipActive : ""}`}>{b.toUpperCase()}</span>
              ))}
            </div>

            {error && <div className={classes.error}>{error}</div>}

            <button className={classes.cta} onClick={handleSave} disabled={submitting}>
              {submitting ? <Loader size={18} /> : <CreditCard size={18} />}
              {submitting ? i18n.t("onboarding.changeCard.processing") : i18n.t("onboarding.changeCard.save")}
            </button>
            <div className={classes.secure}><ShieldCheck size={13} /> {i18n.t("onboarding.changeCard.secureNote")}</div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default ChangeCardModal;
